/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useCallback,
  useState,
  useContext,
  useRef,
  useEffect,
} from 'react';
import { useDropzone } from 'react-dropzone';
import styled, { createGlobalStyle } from 'styled-components';
import csv from 'csv';
import { useMutation } from '@apollo/react-hooks';
import Select from 'react-select';
import { CSVLink } from 'react-csv';
import toast from 'react-hot-toast';
import CreatableSelect from 'react-select/creatable';
import ReactTooltip from 'react-tooltip';
import colors from '../../styles/colors';
import Icon from '../Icon';
import { IconEnum, IconEnum as Icons } from '../Icons';
import Button from '../Button';
import { CREATE_CONTACTS, GET_CURRENT_ORGANIZATION } from '../../graphql/queries';
import { AppContext } from '../../context/AppContext';
import { getHeaders } from '../customers/Customers';
import { UserContext } from '../../context/UserContext';
import { useQuery } from '@apollo/react-hooks';

const TooltipStyles = createGlobalStyle`
.__react_component_tooltip{
      display: inline-block !important;
      max-width: 300px;
    };
.css-1pahdxg-control{
      border-color: ${colors.grey3} !important;
      box-shadow: none !important;
      min-height: 40px;
    }
`;

const Container = styled.div`
  padding: 20px 15px;
  width: 30vw;
  @media screen and (max-width: 991px) {
    width: 50vw;
  }

  @media screen and (max-width: 450px) {
    width: calc(75vw - 20px);
    max-height: 70vh;
  }
`;

const customStyles = {
  option: (provided) => ({
    ...provided,
    fontSize: '13px',

  }),
};

const UploadText = styled.div`
  margin-bottom: 10px;
  font-weight: 500;
  font-size: 1.2rem;
  color: ${colors.grey2};
`;

const DropzoneContainer = styled.div`
  width: 100%;
  background: rgba(26, 188, 156, 0.05);
  height: 225px;
  border: 1px solid #1abc9c;
  box-sizing: border-box;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const DropzoneText = styled.div`
  margin-top: 15px;
  font-weight: 500;
  font-size: 1.2rem;
  color: ${colors.seaGreen};
`;

const Error = styled.div`
  margin-bottom: 10px;
  font-weight: 500;
  font-size: 1.2rem;
  color: ${colors.red};
`;

const Form = styled.form``;

const ColumnPickContainer = styled.div`
  width: 100%;
  display: flex;
`;

const SelectContainer = styled.div`
  flex: 1;
`;

const IconContainer = styled.div`
  height: auto;
  display: flex;
  align-items: center;
  margin: 0px 15px;
  /* width: 100%; */
`;

const PhoneNumberBox = styled.div`
  border: 1px solid #e0e0e0;
  box-sizing: border-box;
  border-radius: 5px;
  color: ${colors.grey3};
  background: ${colors.grey6};
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 1.2rem;
  flex: 1;
  text-align: left;
  padding-left: 15px;
`;

const InvalidContactsContainer = styled.div`
  display: flex;
  padding: 5px 0;
  justify-content: space-between;
  margin-top: 20px;
  @media screen and (max-width: 680px) {
    justify-content: center;
    align-items: center;
    flex-direction: column;
  } ;
`;

const Text = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  padding: 5px 0;
`;

const MessageDetails = styled.div`
  display: flex;
  font-size: 1.2rem;
  color: ${colors.grey3};
  padding: 0 5px;
  margin: 10px 0;
`;

export default function ImportContactListModal({ close }) {
  //state
  const { app, setApp } = useContext(AppContext);
  const { user } = useContext(UserContext);
  const [error, setError] = useState('');
  const [csvData, setCSVData] = useState('');
  const [contacts, setContacts] = useState([]);
  const [csvKeys, setCsvKeys] = useState(null);
  const [phoneColumn, setPhoneColumn] = useState({ value: '', label: '' });
  const [invalidContacts, setInvalidContacts] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [exportData, setExportData] = useState([]);
  const csvRef = useRef();

  const { data: orgData } = useQuery(GET_CURRENT_ORGANIZATION, {
    fetchPolicy: 'cache-first',
  });

  const referralMarketingEnabled = orgData?.getCurrentOrganization?.referralMarketingEnabled ?? true;
  const smsCampaignsEnabled = !!(orgData?.getCurrentOrganization?.smsCampaign?.OptInAt);

  // Mutation
  const [createContacts, { loading }] = useMutation(CREATE_CONTACTS, {
    variables: {
      phoneNumber: contacts,
      tags: selectedTags.map((i) => i.value),
    },
    onError(error) {
      console.log(error);
    },
    async onCompleted() {
      if (app?.refetchContacts) {
        await setApp({ ...app, paginationOffset: 1, searchQuery: null });
        await app?.refetchContacts();
      }
      close();
    },
  });

  const onDrop = useCallback((acceptedFiles) => {
    const reader = new FileReader();
    reader.onabort = () => console.log('file reading was aborted');
    reader.onerror = () => console.log('file reading failed');
    reader.onload = () => {
      // Removing strange character
      const response = reader.result.replace('ï»¿', '');
      // Parse CSV file and convert to json
      csv.parse(response, { relax_column_count: true }, (err, data) => {
        if (!data || err) {
          setError('Invalid file format. File must be a valid CSV file.');
          return;
        }
        setCsvKeys(data[0]);
        const keys = data.shift();
        data = data.map((row) => {
          return keys.reduce((obj, key, i) => {
            obj[key] = row[i];
            return obj;
          }, {});
        });
        setError('');
        setCSVData(data);
      });
    };

    // read file contents
    acceptedFiles.forEach((file) => reader.readAsText(file));
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept:
      '.csv, text/csv, application/vnd.ms-excel, application/csv, text/x-csv, application/x-csv, text/comma-separated-values, text/x-comma-separated-values',
  });

  const isValidContact = (contact = '', selectedColumn) => {
    const numberToSend = contact[selectedColumn]
        ?.replace(/[^0-9]/g, "") // eslint-disable-line
      .trim() || '';

    const withCountryCode = numberToSend.split('')[0] === '1';
    if (
      (withCountryCode && numberToSend.length !== 11)
      || (!withCountryCode && numberToSend.length !== 10)
    ) {
      return false;
    }
    return numberToSend;
  };

  const setValidateTagValue = (newValue = '') => {
    const regex = /^[a-z0-9-]{1,}$/;
    const isValid = regex?.test(newValue);
    if (isValid) {
      const tagsWithNew = [
        ...selectedTags,
        { label: newValue, value: newValue },
      ];
      setTags((old) => [...old, { label: newValue, value: newValue }] || []);
      return setSelectedTags(tagsWithNew || []);
    }
    toast.error('Please enter a valid contact tag');
  };

  const filterContacts = (allContacts, selectedColumn) => {
    const validContacts = allContacts.filter((contact) => isValidContact(contact, selectedColumn));
    const invalidContacts = allContacts.filter(
      (contact) => !isValidContact(contact, selectedColumn),
    );
    return { validContacts, invalidContacts };
  };

  //download invalid contacts  csv
  useEffect(() => {
    if (
      exportData.length > 0
      && csvRef
      && csvRef.current
      && csvRef.current.link
    ) {
      setTimeout(() => {
        csvRef.current.link.click();
        setExportData([]);
      });
    }
  }, [exportData]);

  // Download csv file
  const downloadExportData = () => {
    if (invalidContacts?.length > 0 && csvRef?.current?.link?.click) {
      csvRef.current.link.click();
    }
  };

  useEffect(() => {
    if (user?.organization?.tags) {
      const tagsData = user?.organization?.tags.map((tag) => {
        return {
          label: tag,
          value: tag,
        };
      });
      setTags(tagsData);
    }
  }, [user]);

  if (csvData) {
    const options = csvKeys.map((item) => {
      return { value: item, label: item };
    });

    return (
      <Container>
        <TooltipStyles />
        <CSVLink
          data={invalidContacts.map((row) => {
            const baseData = {
              phoneNumber: row[phoneColumn.value],
              createdAt: row['CREATED AT'],
              lastImported: row['LAST IMPORT'],
              originalSource: row['ORIGINAL SOURCE'],
              optInMethod: row['OPT-IN METHOD'],
              lastOptIn: row['LAST OPT-IN'],
              optOut: row['OPT-OUT'],
            };

            // Add SMS campaign opt-out if enabled
            if (smsCampaignsEnabled) {
              baseData.smsCampaignOptOut = row['SMS OPT-OUT'];
            }

            // Add referral marketing fields if enabled
            if (referralMarketingEnabled) {
              return {
                ...baseData,
                inWelcomeQueue: row['IN WELCOME QUEUE'],
                currentAvailableBalance: row['CURRENT AVAILABLE BALANCE'],
                totalCreditGranted: row['TOTAL CREDIT GRANTED'],
                totalCreditRedeemed: row['TOTAL CREDIT REDEEMED'],
                totalCouponsGranted: row['TOTAL DISCOUNTS GRANTED'],
                totalCouponsRedeemed: row['TOTAL DISCOUNTS REDEEMED'],
                totalCouponsAcceptedRef: row['TOTAL REF DISCOUNTS ACCEPTED'],
                totalRefClicks: row['TOTAL REF CLICKS'],
                totalCouponsRedRef: row['TOTAL REF DISCOUNTS REDEEMED'],
                totalConversionRateRef: row['TOTAL REF CONVERSION RATE'],
              };
            }
            return baseData;
          })}
          headers={getHeaders(referralMarketingEnabled, smsCampaignsEnabled)}
          filename="Contacts.csv"
          ref={csvRef}
          target="_self"
        />
        <Form
          onSubmit={(e) => {
            e.preventDefault();

            if (contacts.length > 0) {
              createContacts();
            }
          }}
        >
          <UploadText>Map columns</UploadText>
          <ColumnPickContainer>
            <SelectContainer>
              <Select
                defaultValue={phoneColumn}
                value={phoneColumn}
                onChange={(val) => {
                  setPhoneColumn(val);
                  const filteredData = filterContacts(csvData, val.value);
                  const validContacts = filteredData?.validContacts?.filter(
                    (contact) => contact?.PHONE !== '',
                  );
                  const invalidContacts = filteredData?.invalidContacts?.filter(
                    (contact) => contact?.PHONE !== '',
                  );
                  setContacts(
                    validContacts.map((item) => {
                      return item[val.value];
                    }),
                  );

                  setInvalidContacts(invalidContacts);
                }}
                options={options}
              />
            </SelectContainer>
            <IconContainer>
              <Icon icon={Icons.RightArrow} size={14} />
            </IconContainer>
            <PhoneNumberBox>Phone Number</PhoneNumberBox>
          </ColumnPickContainer>

          <InvalidContactsContainer>
            <Text>Invalid Contacts Count: {invalidContacts?.length}</Text>
            <Button
              margin="0px 0px 0px 0px"
              text={`DOWNLOAD CSV (${invalidContacts.length})`}
              type="button"
              onClick={() => {
                downloadExportData();
              }}
              width="max-content"
              height="30px"
              padding="0 24px"
            />
          </InvalidContactsContainer>

          <div style={{ marginTop: '0px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '0px',
                width: 'max-content',
              }}
              data-tip="Tags should contain only lower-case letters, numbers, and hyphens (no other special characters or spaces allowed)."
            >
              <MessageDetails>Tags</MessageDetails>{' '}
              <Icon size={10} margin="0 5px" icon={IconEnum.infoRegular} />
            </div>
            <CreatableSelect
              onChange={(updatedTags) => setSelectedTags(updatedTags || [])}
              options={tags}
              isClearable={false}
              isMulti={true}
              value={selectedTags}
              onCreateOption={setValidateTagValue}
              styles={customStyles}
            />
          </div>

          <Button
            margin="20px 0px 0px 0px"
            text={`ADD VALID CONTACTS (${contacts.length})`}
            type="submit"
            width="100%"
            padding="0 10px"
            loading={loading}
          />
        </Form>
        <TooltipStyles />
        <ReactTooltip place="right" type="dark" effect="solid" />
      </Container>
    );
  }

  return (
    <Container>
      {error ? (
        <Error>{error}</Error>
      ) : (
        <UploadText>Upload a CSV file (.csv file format)</UploadText>
      )}
      <DropzoneContainer {...getRootProps()}>
        <input {...getInputProps()} />
        <Icon icon={Icons.UploadRegular} inactiveColor={colors.seaGreen} />
        <DropzoneText>CLICK HERE TO UPLOAD FILE</DropzoneText>
      </DropzoneContainer>
    </Container>
  );
}
